import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { QueryKey } from '@tanstack/react-query'
import { DeviceLinkModel } from '../models'
import { getDeviceLinks } from '../api'
import { TimeInMilliseconds } from 'src/Libs/types'
import { isUndefined } from 'lodash'
import { usePortalQuery } from 'src/Libs/api'

interface UseGetDeviceLinks {
  deviceLinks: DeviceLinkModel[] | null
  isLoading: boolean
  error?: Error | AuthFetchError
  hasError: boolean
}

const MAX_RETRY_ATTEMPTS = 2

export const getDeviceLinksQueryKey = (installationId: string | undefined): QueryKey => ['device-links', installationId]

/**
 * Hook for handling device links of an installation
 */
export const useGetDeviceLinks = (installationId: string | undefined): UseGetDeviceLinks => {
  const authFetch = useAuthFetchWithResponse()

  const queryFn = async (): Promise<DeviceLinkModel[] | null> => {
    const { error: fetchError, hasErrors, value } = await getDeviceLinks(authFetch, installationId)

    if (hasErrors || fetchError) {
      throw new Error(fetchError?.message)
    }
    return value
  }

  const { data, isLoading, isError } = usePortalQuery({
    queryKey: getDeviceLinksQueryKey(installationId),
    queryFn,
    retry: MAX_RETRY_ATTEMPTS,
    /** retries after 0.5 and 1 */
    retryDelay: attempt => {
      return Math.min(500 * 2 ** attempt, TimeInMilliseconds.thirtySecond)
    },
    staleTime: TimeInMilliseconds.oneHour,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    enabled: !isUndefined(installationId),
  })

  return { deviceLinks: data ?? null, isLoading, hasError: isError }
}
